import { useMemo } from 'react';
import { useAuth as useAuthContext } from '../contexts/AuthContext';

// ─────────────────────────────────────────────────────────────────────────────
// PLAN MODULES (Layer 1) - Broad feature categories
// Source: user.tenantContext.includedModules
// MUST MATCH: Backend SystemConfigSeeder._restoModuleDefinitions
// ─────────────────────────────────────────────────────────────────────────────

export const PLAN_MODULES = {
  // ── 22 Primary module keys (match backend SystemConfigSeeder) ────────────
  POS: 'POS',
  ORDER_MANAGEMENT: 'ORDER_MANAGEMENT',
  LOYALTY_PROGRAM: 'LOYALTY_PROGRAM',
  AUDIT_LOGS: 'AUDIT_LOGS',
  RESERVATION: 'RESERVATION',
  TABLE_AND_LOCATION: 'TABLE_AND_LOCATION',
  RESTAURANT_PROFILE: 'RESTAURANT_PROFILE',
  QR_FLOW: 'QR_FLOW',
  CAPTAIN_APP: 'CAPTAIN_APP',
  INVENTORY: 'INVENTORY',
  KITCHEN_DISPLAY: 'KITCHEN_DISPLAY',
  INTELLIGENT_MENU: 'INTELLIGENT_MENU',
  BASIC_MENU: 'BASIC_MENU',
  WHATSAPP_BUSINESS: 'WHATSAPP_BUSINESS',
  ANALYTICS: 'ANALYTICS',
  REPORT: 'REPORT',
  PROFILE: 'PROFILE',
  SETTINGS: 'SETTINGS',
  BILLING_HISTORY: 'BILLING_HISTORY',
  PROMOTIONAL_CAMPAIGN: 'PROMOTIONAL_CAMPAIGN',
  USER_MANAGEMENT: 'USER_MANAGEMENT',
  AI_CREDITS: 'AI_CREDITS',

  // ── Legacy modules ───────────────────────────────────────────────────────
  HR: 'HR',
  PAYROLL: 'PAYROLL',
  INTEGRATIONS: 'INTEGRATIONS',

  // ── Legacy aliases (backward compat with old module key strings) ─────────
  DASHBOARD: 'POS',
  ORDERS: 'ORDER_MANAGEMENT',
  KDS: 'KITCHEN_DISPLAY',
  TABLES: 'TABLE_AND_LOCATION',
  RESERVATIONS: 'RESERVATION',
  QR_CODES: 'QR_FLOW',
  LOYALTY: 'LOYALTY_PROGRAM',
  WHATSAPP: 'WHATSAPP_BUSINESS',
  PROMOTIONS: 'PROMOTIONAL_CAMPAIGN',
  MARKETING: 'PROMOTIONAL_CAMPAIGN',
  REPORTS: 'REPORT',
  BILLING: 'BILLING_HISTORY',
  STAFF: 'USER_MANAGEMENT',

  // ── Route-level aliases used in App.tsx AccessProtectedRoute ─────────────
  USERS: 'USER_MANAGEMENT',           // /user-management route
  MENU: 'BASIC_MENU',                 // generic menu alias
  MENU_BASIC: 'BASIC_MENU',           // basic menu route
  MENU_INTELLIGENT: 'INTELLIGENT_MENU', // intelligent menu route
  AUDIT: 'AUDIT_LOGS',                // /audit-logs route
  PURCHASE_ORDERS: 'INVENTORY',       // /inventory/purchase (sub-feature of INVENTORY)
  RECIPES: 'INVENTORY',               // /inventory/consumption (recipes = inventory)
  SUPPLIERS: 'INVENTORY',             // /inventory/suppliers (sub-feature of INVENTORY)
  WASTAGE: 'INVENTORY',               // /reports/wastage-report
  REPORTS_SALES: 'REPORT',            // sales reports sub-routes
  REPORTS_FINANCIAL: 'REPORT',        // financial reports sub-routes
  REPORTS_INVENTORY: 'REPORT',        // inventory reports sub-routes
  REPORTS_BASIC: 'REPORT',            // basic reports alias
} as const;

export type PlanModuleKey = keyof typeof PLAN_MODULES;
/** Widened to string so legacy module key literals (e.g. 'LOYALTY') still compile.
 *  Runtime normalization in accessControl.service handles old→new key mapping. */
export type PlanModuleValue = string;

// ─────────────────────────────────────────────────────────────────────────────
// USER PERMISSIONS (Layer 2) - Granular actions
// Source: user.permissions.grantedPermissions
// MUST MATCH: Backend Permissions.cs constants
// ─────────────────────────────────────────────────────────────────────────────

export const PERMISSIONS = {
  // ── POS (module-level) ────────────────────────────────────────────────────
  POS: 'POS',

  // ── Order Management (module-level) ──────────────────────────────────────
  ORDER_MANAGEMENT: 'ORDER_MANAGEMENT',

  // ── Kitchen Display (module-level) ────────────────────────────────────────
  KITCHEN_DISPLAY: 'KITCHEN_DISPLAY',
  KDS: 'KITCHEN_DISPLAY',                        // legacy alias

  // ── Captain App (module-level) ────────────────────────────────────────────
  CAPTAIN_APP: 'CAPTAIN_APP',

  // ── Loyalty Program ───────────────────────────────────────────────────────
  LOYALTY_MANUAL_ADJUST: 'LOYALTY_MANUAL_ADJUST',
  LOYALTY_CREATE_REWARDS: 'LOYALTY_CREATE_REWARDS',

  // ── Reservation ───────────────────────────────────────────────────────────
  RESERVATION_FULL_ACCESS: 'RESERVATION_FULL_ACCESS',
  RESERVATIONS_MANAGE: 'RESERVATION_FULL_ACCESS', // legacy alias

  // ── Table and Table Location ──────────────────────────────────────────────
  TABLE_FULL_ACCESS: 'TABLE_FULL_ACCESS',
  TABLE_MANAGE: 'TABLE_FULL_ACCESS',              // legacy alias

  // ── Restaurant Profile ────────────────────────────────────────────────────
  RESTAURANT_FULL_ACCESS: 'RESTAURANT_FULL_ACCESS',
  RESTAURANT_PROFILE_MANAGE: 'RESTAURANT_FULL_ACCESS', // legacy alias

  // ── Inventory ─────────────────────────────────────────────────────────────
  INVENTORY_DASHBOARD_ACCESS: 'INVENTORY_DASHBOARD_ACCESS',
  INVENTORY_DASHBOARD_VIEW: 'INVENTORY_DASHBOARD_ACCESS',  // legacy alias
  OVERWRITE_INGREDIENT_THRESHOLD: 'OVERWRITE_INGREDIENT_THRESHOLD',
  INVENTORY_INVOICE_ACCESS: 'INVENTORY_INVOICE_ACCESS',
  INVENTORY_REPORTS_ACCESS: 'INVENTORY_REPORTS_ACCESS',
  STOCK_CONSUMPTION_ACCESS: 'STOCK_CONSUMPTION_ACCESS',
  INVENTORY_STOCK_MANAGE: 'STOCK_CONSUMPTION_ACCESS',      // legacy alias
  PURCHASE_ORDER_ACCESS: 'PURCHASE_ORDER_ACCESS',
  PURCHASE_ORDER_MANAGE: 'PURCHASE_ORDER_ACCESS',          // legacy alias
  EDIT_INGREDIENT_ACCESS: 'EDIT_INGREDIENT_ACCESS',
  INVENTORY_INGREDIENT_MANAGE: 'EDIT_INGREDIENT_ACCESS',   // legacy alias

  // ── Menu (Basic + Intelligent) ────────────────────────────────────────────
  MENU_FULL_ACCESS: 'MENU_FULL_ACCESS',
  MENU_COST_MANAGEMENT: 'MENU_COST_MANAGEMENT',
  BASIC_MENU_MANAGE: 'MENU_FULL_ACCESS',          // legacy alias
  INTELLIGENT_MENU_MANAGE: 'MENU_FULL_ACCESS',    // legacy alias
  INTELLIGENT_MENU_COSTING: 'MENU_COST_MANAGEMENT', // legacy alias

  // ── Analytics ─────────────────────────────────────────────────────────────
  FINANCIAL_ANALYTICS: 'FINANCIAL_ANALYTICS',
  SALES_ANALYTICS: 'SALES_ANALYTICS',
  INVENTORY_ANALYTICS: 'INVENTORY_ANALYTICS',
  CUSTOMER_ANALYTICS: 'CUSTOMER_ANALYTICS',
  ANALYTICS_FINANCIAL: 'FINANCIAL_ANALYTICS',     // legacy alias
  ANALYTICS_SALES: 'SALES_ANALYTICS',             // legacy alias
  ANALYTICS_INVENTORY: 'INVENTORY_ANALYTICS',     // legacy alias
  ANALYTICS_CUSTOMER: 'CUSTOMER_ANALYTICS',       // legacy alias

  // ── Reports ───────────────────────────────────────────────────────────────
  FINANCIAL_REPORTS: 'FINANCIAL_REPORTS',
  SALES_REPORTS: 'SALES_REPORTS',
  INVENTORY_REPORTS: 'INVENTORY_REPORTS',
  CUSTOMER_REPORTS: 'CUSTOMER_REPORTS',
  REPORTS_FINANCIAL: 'FINANCIAL_REPORTS',         // legacy alias
  REPORTS_SALES: 'SALES_REPORTS',                 // legacy alias
  REPORTS_INVENTORY: 'INVENTORY_REPORTS',         // legacy alias
  REPORTS_CUSTOMER: 'CUSTOMER_REPORTS',           // legacy alias

  // ── Profile ───────────────────────────────────────────────────────────────
  PROFILE_FULL_ACCESS: 'PROFILE_FULL_ACCESS',

  // ── Settings ─────────────────────────────────────────────────────────────
  SETTINGS_FULL_ACCESS: 'SETTINGS_FULL_ACCESS',
  TAX_CONFIGURATION_SETTINGS: 'TAX_CONFIGURATION_SETTINGS',
  INVENTORY_SETTINGS: 'INVENTORY_SETTINGS',
  CUSTOMER_NOTIFICATION_SETTINGS: 'CUSTOMER_NOTIFICATION_SETTINGS',
  ORDER_SETTINGS: 'ORDER_SETTINGS',
  RESERVATION_SETTINGS: 'RESERVATION_SETTINGS',
  BILLING_SETTINGS: 'BILLING_SETTINGS',
  LOYALTY_PROGRAM_SETTINGS: 'LOYALTY_PROGRAM_SETTINGS',
  // Legacy settings aliases
  SETTINGS_MANAGE: 'SETTINGS_FULL_ACCESS',
  SETTINGS_TAX: 'TAX_CONFIGURATION_SETTINGS',
  SETTINGS_INVENTORY: 'INVENTORY_SETTINGS',
  SETTINGS_NOTIFICATIONS: 'CUSTOMER_NOTIFICATION_SETTINGS',
  SETTINGS_ORDERS: 'ORDER_SETTINGS',
  SETTINGS_RESERVATIONS: 'RESERVATION_SETTINGS',
  SETTINGS_BILLING: 'BILLING_SETTINGS',
  SETTINGS_LOYALTY: 'LOYALTY_PROGRAM_SETTINGS',

  // ── Billing History ───────────────────────────────────────────────────────
  BILLING_HISTORY_FULL_ACCESS: 'BILLING_HISTORY_FULL_ACCESS',
  BILLING_MANAGE: 'BILLING_HISTORY_FULL_ACCESS',  // legacy alias

  // ── User Management ───────────────────────────────────────────────────────
  USER_MANAGEMENT_FULL_ACCESS: 'USER_MANAGEMENT_FULL_ACCESS',
  USERS_MANAGE: 'USER_MANAGEMENT_FULL_ACCESS',    // legacy alias

  // ── WhatsApp Business (module-level) ──────────────────────────────────────
  WHATSAPP_BUSINESS: 'WHATSAPP_BUSINESS',

  // ── Audit Logs (module-level) ─────────────────────────────────────────────
  AUDIT_LOGS: 'AUDIT_LOGS',

  // ── QR Flow (module-level) ────────────────────────────────────────────────
  QR_FLOW: 'QR_FLOW',
  GENERATE_QR_CODE: 'QR_FLOW',                   // legacy alias

  // ── Promotional Campaign (module-level) ───────────────────────────────────
  PROMOTIONAL_CAMPAIGN: 'PROMOTIONAL_CAMPAIGN',
  PROMOTIONAL_CAMPAIGNS: 'PROMOTIONAL_CAMPAIGN',  // legacy alias

  // ── AI Credits (module-level) ─────────────────────────────────────────────
  AI_CREDITS: 'AI_CREDITS',

  // ── Staff (legacy aliases for USER_MANAGEMENT) ────────────────────────────
  STAFF_VIEW: 'STAFF_VIEW',
  STAFF_MANAGE: 'STAFF_MANAGE',

  // ── HR & Payroll ──────────────────────────────────────────────────────────
  HR_VIEW: 'HR_VIEW',
  HR_CREATE: 'HR_CREATE',
  HR_EDIT: 'HR_EDIT',
  HR_DELETE: 'HR_DELETE',
  HR_ATTENDANCE_VIEW: 'HR_ATTENDANCE_VIEW',
  HR_ATTENDANCE_MANAGE: 'HR_ATTENDANCE_MANAGE',
  HR_SHIFT_MANAGE: 'HR_SHIFT_MANAGE',
  HR_LEAVE_MANAGE: 'HR_LEAVE_MANAGE',
  PAYROLL_VIEW: 'PAYROLL_VIEW',
  PAYROLL_PROCESS: 'PAYROLL_PROCESS',
  PAYROLL_ADJUST: 'PAYROLL_ADJUST',
  PAYROLL_APPROVE: 'PAYROLL_APPROVE',

  // ── Integrations ──────────────────────────────────────────────────────────
  INTEGRATIONS_VIEW: 'INTEGRATIONS_VIEW',
  INTEGRATIONS_MANAGE: 'INTEGRATIONS_MANAGE',

  // ── Legacy dashboard perms (no longer valid — map to POS module) ──────────
  DASHBOARD_VIEW: 'POS',
  DASHBOARD_ANALYTICS: 'POS',
} as const;

export type PermissionKey = keyof typeof PERMISSIONS;
export type PermissionValue = typeof PERMISSIONS[PermissionKey];

// ─────────────────────────────────────────────────────────────────────────────
// PERMISSION TO MODULE MAPPING
// Maps granular permissions to their parent plan module
// ─────────────────────────────────────────────────────────────────────────────

export const PERMISSION_TO_MODULE: Record<string, PlanModuleValue> = {
  // POS
  [PERMISSIONS.POS]: PLAN_MODULES.POS,
  DASHBOARD_VIEW: PLAN_MODULES.POS,

  // Orders
  [PERMISSIONS.ORDER_MANAGEMENT]: PLAN_MODULES.ORDER_MANAGEMENT,

  // Kitchen Display
  [PERMISSIONS.KITCHEN_DISPLAY]: PLAN_MODULES.KITCHEN_DISPLAY,

  // Captain App
  [PERMISSIONS.CAPTAIN_APP]: PLAN_MODULES.CAPTAIN_APP,

  // Menu - Basic
  [PERMISSIONS.MENU_FULL_ACCESS]: PLAN_MODULES.BASIC_MENU,

  // Menu - Intelligent
  [PERMISSIONS.MENU_COST_MANAGEMENT]: PLAN_MODULES.INTELLIGENT_MENU,

  // Tables
  [PERMISSIONS.TABLE_FULL_ACCESS]: PLAN_MODULES.TABLE_AND_LOCATION,

  // Reservations
  [PERMISSIONS.RESERVATION_FULL_ACCESS]: PLAN_MODULES.RESERVATION,

  // Inventory
  [PERMISSIONS.INVENTORY_DASHBOARD_ACCESS]: PLAN_MODULES.INVENTORY,
  [PERMISSIONS.OVERWRITE_INGREDIENT_THRESHOLD]: PLAN_MODULES.INVENTORY,
  [PERMISSIONS.INVENTORY_INVOICE_ACCESS]: PLAN_MODULES.INVENTORY,
  [PERMISSIONS.INVENTORY_REPORTS_ACCESS]: PLAN_MODULES.INVENTORY,
  [PERMISSIONS.STOCK_CONSUMPTION_ACCESS]: PLAN_MODULES.INVENTORY,
  [PERMISSIONS.PURCHASE_ORDER_ACCESS]: PLAN_MODULES.INVENTORY,
  [PERMISSIONS.EDIT_INGREDIENT_ACCESS]: PLAN_MODULES.INVENTORY,

  // Restaurant Profile
  [PERMISSIONS.RESTAURANT_FULL_ACCESS]: PLAN_MODULES.RESTAURANT_PROFILE,

  // Reports
  [PERMISSIONS.FINANCIAL_REPORTS]: PLAN_MODULES.REPORT,
  [PERMISSIONS.SALES_REPORTS]: PLAN_MODULES.REPORT,
  [PERMISSIONS.INVENTORY_REPORTS]: PLAN_MODULES.REPORT,
  [PERMISSIONS.CUSTOMER_REPORTS]: PLAN_MODULES.REPORT,

  // Analytics
  [PERMISSIONS.FINANCIAL_ANALYTICS]: PLAN_MODULES.ANALYTICS,
  [PERMISSIONS.SALES_ANALYTICS]: PLAN_MODULES.ANALYTICS,
  [PERMISSIONS.INVENTORY_ANALYTICS]: PLAN_MODULES.ANALYTICS,
  [PERMISSIONS.CUSTOMER_ANALYTICS]: PLAN_MODULES.ANALYTICS,

  // Loyalty
  [PERMISSIONS.LOYALTY_MANUAL_ADJUST]: PLAN_MODULES.LOYALTY_PROGRAM,
  [PERMISSIONS.LOYALTY_CREATE_REWARDS]: PLAN_MODULES.LOYALTY_PROGRAM,

  // WhatsApp
  [PERMISSIONS.WHATSAPP_BUSINESS]: PLAN_MODULES.WHATSAPP_BUSINESS,

  // QR Flow
  [PERMISSIONS.QR_FLOW]: PLAN_MODULES.QR_FLOW,

  // Promotions
  [PERMISSIONS.PROMOTIONAL_CAMPAIGN]: PLAN_MODULES.PROMOTIONAL_CAMPAIGN,

  // User Management
  [PERMISSIONS.USER_MANAGEMENT_FULL_ACCESS]: PLAN_MODULES.USER_MANAGEMENT,
  [PERMISSIONS.STAFF_VIEW]: PLAN_MODULES.USER_MANAGEMENT,
  [PERMISSIONS.STAFF_MANAGE]: PLAN_MODULES.USER_MANAGEMENT,

  // Profile
  [PERMISSIONS.PROFILE_FULL_ACCESS]: PLAN_MODULES.PROFILE,

  // HR
  [PERMISSIONS.HR_VIEW]: PLAN_MODULES.HR,
  [PERMISSIONS.HR_CREATE]: PLAN_MODULES.HR,
  [PERMISSIONS.HR_EDIT]: PLAN_MODULES.HR,
  [PERMISSIONS.HR_DELETE]: PLAN_MODULES.HR,
  [PERMISSIONS.HR_ATTENDANCE_VIEW]: PLAN_MODULES.HR,
  [PERMISSIONS.HR_ATTENDANCE_MANAGE]: PLAN_MODULES.HR,
  [PERMISSIONS.HR_SHIFT_MANAGE]: PLAN_MODULES.HR,
  [PERMISSIONS.HR_LEAVE_MANAGE]: PLAN_MODULES.HR,

  // Payroll
  [PERMISSIONS.PAYROLL_VIEW]: PLAN_MODULES.PAYROLL,
  [PERMISSIONS.PAYROLL_PROCESS]: PLAN_MODULES.PAYROLL,
  [PERMISSIONS.PAYROLL_ADJUST]: PLAN_MODULES.PAYROLL,
  [PERMISSIONS.PAYROLL_APPROVE]: PLAN_MODULES.PAYROLL,

  // Integrations
  [PERMISSIONS.INTEGRATIONS_VIEW]: PLAN_MODULES.INTEGRATIONS,
  [PERMISSIONS.INTEGRATIONS_MANAGE]: PLAN_MODULES.INTEGRATIONS,

  // Settings
  [PERMISSIONS.SETTINGS_FULL_ACCESS]: PLAN_MODULES.SETTINGS,
  [PERMISSIONS.TAX_CONFIGURATION_SETTINGS]: PLAN_MODULES.SETTINGS,
  [PERMISSIONS.INVENTORY_SETTINGS]: PLAN_MODULES.SETTINGS,
  [PERMISSIONS.CUSTOMER_NOTIFICATION_SETTINGS]: PLAN_MODULES.SETTINGS,
  [PERMISSIONS.ORDER_SETTINGS]: PLAN_MODULES.SETTINGS,
  [PERMISSIONS.RESERVATION_SETTINGS]: PLAN_MODULES.SETTINGS,
  [PERMISSIONS.BILLING_SETTINGS]: PLAN_MODULES.SETTINGS,
  [PERMISSIONS.LOYALTY_PROGRAM_SETTINGS]: PLAN_MODULES.SETTINGS,

  // Audit
  [PERMISSIONS.AUDIT_LOGS]: PLAN_MODULES.AUDIT_LOGS,

  // Billing History
  [PERMISSIONS.BILLING_HISTORY_FULL_ACCESS]: PLAN_MODULES.BILLING_HISTORY,

  // AI Credits
  [PERMISSIONS.AI_CREDITS]: PLAN_MODULES.AI_CREDITS,
};

/**
 * Get the parent module for a permission
 * Falls back to extracting from permission name if not mapped
 */
export const getModuleForPermission = (permission: string): string => {
  return PERMISSION_TO_MODULE[permission] || permission.split('_')[0];
};

// ─────────────────────────────────────────────────────────────────────────────
// ACCESS RESULT TYPE
// ─────────────────────────────────────────────────────────────────────────────

export type AccessResult = {
  allowed: boolean;
  reason: 'allowed' | 'plan_blocked' | 'permission_denied' | 'loading';
  requiredModule?: string;
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN HOOK: useAccessControl
// Checks BOTH layers: Plan Module + User Permission
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Two-layer access control hook
 * 
 * Layer 1 (Plan): Checks if the plan includes the module
 * Layer 2 (User): Checks if the user has the specific permission
 * 
 * @example
 * ```tsx
 * const access = useAccessControl('INVENTORY_VIEW');
 * 
 * if (!access.allowed) {
 *   if (access.reason === 'plan_blocked') {
 *     return <UpgradePrompt module={access.requiredModule} />;
 *   }
 *   return null; // Permission denied - hide element
 * }
 * 
 * return <InventorySection />;
 * ```
 */
export const useAccessControl = (permission: string): AccessResult => {
  const { user, includedModules: contextModules } = useAuthContext();

  // Get plan modules from context/user/localStorage
  const planModules = useMemo((): string[] => {
    if (contextModules && contextModules.length > 0) {
      return contextModules;
    }
    if (user?.tenantContext?.includedModules && user.tenantContext.includedModules.length > 0) {
      return user.tenantContext.includedModules;
    }
    try {
      const stored = localStorage.getItem('includedModules');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch {
      // Ignore
    }
    return [];
  }, [user, contextModules]);

  // Get user permissions from user object/localStorage
  const userPermissions = useMemo((): string[] => {
    if (user?.permissions?.grantedPermissions && user.permissions.grantedPermissions.length > 0) {
      return user.permissions.grantedPermissions;
    }
    try {
      // Try grantedPermissions first (lean login format)
      const grantedStored = localStorage.getItem('grantedPermissions');
      if (grantedStored) {
        const parsed = JSON.parse(grantedStored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
      // Fall back to permissions object (standard login format)
      const stored = localStorage.getItem('permissions');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.grantedPermissions && Array.isArray(parsed.grantedPermissions)) {
          return parsed.grantedPermissions;
        }
      }
    } catch {
      // Ignore
    }
    return [];
  }, [user]);

  // Check if data is still loading
  const isLoading = !user && planModules.length === 0 && userPermissions.length === 0;

  return useMemo((): AccessResult => {
    // If still loading, allow access (avoid flicker)
    if (isLoading) {
      return { allowed: true, reason: 'loading' };
    }

    // Get the module this permission belongs to
    const requiredModule = getModuleForPermission(permission);

    // LAYER 1: Check if plan includes the MODULE
    // If no modules loaded, allow access (backend hasn't sent data yet)
    if (planModules.length > 0 && !planModules.includes(requiredModule)) {
      return {
        allowed: false,
        reason: 'plan_blocked',
        requiredModule
      };
    }

    // LAYER 2: Check if user has the specific PERMISSION
    // If no permissions loaded, allow access (backend hasn't sent data yet)
    if (userPermissions.length > 0 && !userPermissions.includes(permission)) {
      return {
        allowed: false,
        reason: 'permission_denied',
        requiredModule
      };
    }

    return { allowed: true, reason: 'allowed' };
  }, [permission, planModules, userPermissions, isLoading]);
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPER HOOK: useModuleAccess
// Checks ONLY Layer 1 (Plan Module) - useful for sidebar visibility
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Check if a plan MODULE is included (Layer 1 only)
 * Use this for sidebar visibility checks
 * 
 * @example
 * ```tsx
 * const hasInventoryModule = useModuleAccess('INVENTORY');
 * 
 * if (!hasInventoryModule) {
 *   return <LockedNavItem label="Inventory" />;
 * }
 * return <Link to="/inventory">Inventory</Link>;
 * ```
 */
export const useModuleAccess = (moduleName: string): boolean => {
  const { user, includedModules: contextModules } = useAuthContext();

  const planModules = useMemo((): string[] => {
    if (contextModules && contextModules.length > 0) {
      return contextModules;
    }
    if (user?.tenantContext?.includedModules && user.tenantContext.includedModules.length > 0) {
      return user.tenantContext.includedModules;
    }
    try {
      const stored = localStorage.getItem('includedModules');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch {
      // Ignore
    }
    return [];
  }, [user, contextModules]);

  // If no modules loaded, allow access (don't block until backend responds)
  if (planModules.length === 0) {
    return true;
  }

  return planModules.includes(moduleName);
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPER HOOK: useHasPermission
// Checks ONLY Layer 2 (User Permission) - for checking specific actions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Check if user has a specific PERMISSION (Layer 2 only)
 * Use this for action-level checks (e.g., can user apply discount?)
 * 
 * @example
 * ```tsx
 * const canApplyDiscount = useHasPermission('POS_DISCOUNT');
 * 
 * {canApplyDiscount && <DiscountButton />}
 * ```
 */
export const useHasPermission = (permission: string): boolean => {
  const { user } = useAuthContext();

  const userPermissions = useMemo((): string[] => {
    if (user?.permissions?.grantedPermissions && user.permissions.grantedPermissions.length > 0) {
      return user.permissions.grantedPermissions;
    }
    try {
      // Try grantedPermissions first (lean login format)
      const grantedStored = localStorage.getItem('grantedPermissions');
      if (grantedStored) {
        const parsed = JSON.parse(grantedStored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
      // Fall back to permissions object (standard login format)
      const stored = localStorage.getItem('permissions');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.grantedPermissions && Array.isArray(parsed.grantedPermissions)) {
          return parsed.grantedPermissions;
        }
      }
    } catch {
      // Ignore
    }
    return [];
  }, [user]);

  // If no permissions loaded, allow access
  if (userPermissions.length === 0) {
    return true;
  }

  return userPermissions.includes(permission);
};

// ─────────────────────────────────────────────────────────────────────────────
// FULL ACCESS CHECK HOOK
// Returns detailed access information for complex UI decisions
// ─────────────────────────────────────────────────────────────────────────────

export type FullAccessResult = {
  allowed: boolean;
  planAllows: boolean;
  userAllowed: boolean;
  planBlocked: boolean;
  permissionDenied: boolean;
  requiredModule: string;
  isLoading: boolean;
};

/**
 * Full access check with detailed breakdown
 * 
 * @example
 * ```tsx
 * const { allowed, planBlocked, requiredModule } = useFullAccessCheck('INVENTORY_VIEW');
 * 
 * if (planBlocked) {
 *   return <LockedFeature message={`Upgrade to unlock ${requiredModule}`} />;
 * }
 * if (!allowed) return null;
 * return <InventoryDashboard />;
 * ```
 */
export const useFullAccessCheck = (permission: string): FullAccessResult => {
  const { user, includedModules: contextModules } = useAuthContext();

  const planModules = useMemo((): string[] => {
    if (contextModules && contextModules.length > 0) {
      return contextModules;
    }
    if (user?.tenantContext?.includedModules && user.tenantContext.includedModules.length > 0) {
      return user.tenantContext.includedModules;
    }
    try {
      const stored = localStorage.getItem('includedModules');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch {
      // Ignore
    }
    return [];
  }, [user, contextModules]);

  const userPermissions = useMemo((): string[] => {
    if (user?.permissions?.grantedPermissions && user.permissions.grantedPermissions.length > 0) {
      return user.permissions.grantedPermissions;
    }
    try {
      // Try grantedPermissions first (lean login format)
      const grantedStored = localStorage.getItem('grantedPermissions');
      if (grantedStored) {
        const parsed = JSON.parse(grantedStored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
      // Fall back to permissions object (standard login format)
      const stored = localStorage.getItem('permissions');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.grantedPermissions && Array.isArray(parsed.grantedPermissions)) {
          return parsed.grantedPermissions;
        }
      }
    } catch {
      // Ignore
    }
    return [];
  }, [user]);

  const isLoading = !user && planModules.length === 0 && userPermissions.length === 0;
  const requiredModule = getModuleForPermission(permission);

  // Layer 1: Plan allows (or no data yet)
  const planAllows = planModules.length === 0 || planModules.includes(requiredModule);

  // Layer 2: User has permission (or no data yet)
  const userAllowed = userPermissions.length === 0 || userPermissions.includes(permission);

  return {
    allowed: planAllows && userAllowed,
    planAllows,
    userAllowed,
    planBlocked: !planAllows,
    permissionDenied: planAllows && !userAllowed,
    requiredModule,
    isLoading,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// ROUTE ACCESS MAPPING
// Maps routes to required permissions for route guards
// ─────────────────────────────────────────────────────────────────────────────

export const ROUTE_PERMISSION_MAP: Record<string, string | string[]> = {
  // POS & Orders (legacy dashboard routes → POS)
  '/main': PERMISSIONS.POS,
  '/dashboard': PERMISSIONS.POS,
  '/pos': PERMISSIONS.POS,
  '/order-management': PERMISSIONS.ORDER_MANAGEMENT,
  '/kitchen-display': PERMISSIONS.KITCHEN_DISPLAY,
  '/captain-app': PERMISSIONS.CAPTAIN_APP,

  // Menu
  '/menu-management': PERMISSIONS.MENU_FULL_ACCESS,
  '/intelligent-menu': PERMISSIONS.MENU_FULL_ACCESS,
  '/add-edit-dish': PERMISSIONS.MENU_FULL_ACCESS,

  // Tables & Reservations
  '/tables': PERMISSIONS.TABLE_FULL_ACCESS,
  '/table-locations': PERMISSIONS.TABLE_FULL_ACCESS,
  '/table-system': PERMISSIONS.TABLE_FULL_ACCESS,
  '/reservation-management': PERMISSIONS.RESERVATION_FULL_ACCESS,
  '/qr-generator': PERMISSIONS.QR_FLOW,

  // Inventory
  '/inventory': PERMISSIONS.INVENTORY_DASHBOARD_ACCESS,
  '/inventory/management': PERMISSIONS.EDIT_INGREDIENT_ACCESS,
  '/inventory/stock-movements': PERMISSIONS.STOCK_CONSUMPTION_ACCESS,
  '/inventory/alerts': PERMISSIONS.INVENTORY_DASHBOARD_ACCESS,
  '/inventory/consumption': PERMISSIONS.STOCK_CONSUMPTION_ACCESS,
  '/inventory/purchase': PERMISSIONS.PURCHASE_ORDER_ACCESS,
  '/inventory/reports': PERMISSIONS.INVENTORY_REPORTS_ACCESS,

  // Restaurant Profile & Settings
  '/restaurant-profile': PERMISSIONS.RESTAURANT_FULL_ACCESS,
  '/settings': PERMISSIONS.SETTINGS_FULL_ACCESS,
  '/admin-profile': PERMISSIONS.PROFILE_FULL_ACCESS,

  // Reports
  '/reports': PERMISSIONS.SALES_REPORTS,
  '/reports/daily-sales': PERMISSIONS.SALES_REPORTS,
  '/reports/weekly-monthly-summary': PERMISSIONS.SALES_REPORTS,
  '/reports/pnl-summary': PERMISSIONS.FINANCIAL_REPORTS,
  '/reports/tax-gst': PERMISSIONS.FINANCIAL_REPORTS,
  '/reports/item-sales': PERMISSIONS.SALES_REPORTS,
  '/reports/category-sales': PERMISSIONS.SALES_REPORTS,
  '/reports/cancelled-bills': PERMISSIONS.SALES_REPORTS,
  '/reports/cancelled-orders': PERMISSIONS.SALES_REPORTS,
  '/reports/expense-report': PERMISSIONS.FINANCIAL_REPORTS,
  '/reports/wastage-report': PERMISSIONS.INVENTORY_REPORTS,
  '/reports/customer-visit': PERMISSIONS.CUSTOMER_REPORTS,
  '/reports/reward-catalog': PERMISSIONS.LOYALTY_MANUAL_ADJUST,
  '/reports/staff-performance': PERMISSIONS.SALES_REPORTS,

  // Loyalty
  '/loyalty': PERMISSIONS.LOYALTY_MANUAL_ADJUST,
  '/loyalty-settings': PERMISSIONS.LOYALTY_MANUAL_ADJUST,

  // HR & User Management
  '/hr': PERMISSIONS.HR_VIEW,
  '/user-management': PERMISSIONS.USER_MANAGEMENT_FULL_ACCESS,
  '/role-management': PERMISSIONS.USER_MANAGEMENT_FULL_ACCESS,

  // Audit
  '/audit': PERMISSIONS.AUDIT_LOGS,
  '/audit-logs': PERMISSIONS.AUDIT_LOGS,

  // WhatsApp
  '/whatsapp-management': PERMISSIONS.WHATSAPP_BUSINESS,
  '/whatsapp-suggestions': PERMISSIONS.WHATSAPP_BUSINESS,
  '/whatsapp-templates': PERMISSIONS.WHATSAPP_BUSINESS,
  '/whatsapp-campaigns': PERMISSIONS.WHATSAPP_BUSINESS,
  '/whatsapp-recharge': PERMISSIONS.WHATSAPP_BUSINESS,
  '/whatsapp-billing': PERMISSIONS.WHATSAPP_BUSINESS,

  // Marketing / Promotions
  '/marketing': PERMISSIONS.PROMOTIONAL_CAMPAIGN,
  '/campaigns': PERMISSIONS.PROMOTIONAL_CAMPAIGN,

  // Billing & AI
  '/billing': PERMISSIONS.BILLING_HISTORY_FULL_ACCESS,
  '/ai-credits': PERMISSIONS.AI_CREDITS,

  // Integrations
  '/integrations': PERMISSIONS.INTEGRATIONS_VIEW,
};

/**
 * Get the required permission for a route
 */
export const getPermissionForRoute = (route: string): string | string[] | undefined => {
  // Try exact match first
  if (ROUTE_PERMISSION_MAP[route]) {
    return ROUTE_PERMISSION_MAP[route];
  }

  // Try prefix match for nested routes
  const matchingRoute = Object.keys(ROUTE_PERMISSION_MAP)
    .filter(r => route.startsWith(r))
    .sort((a, b) => b.length - a.length)[0];

  return matchingRoute ? ROUTE_PERMISSION_MAP[matchingRoute] : undefined;
};

/**
 * Check if a route is accessible (both layers)
 * Note: Always calls useAccessControl to follow React hooks rules
 */
export const useCanAccessRoute = (route: string): AccessResult => {
  const requiredPermission = getPermissionForRoute(route);

  // Use first permission if multiple are accepted, or empty string if none required
  const permission = requiredPermission
    ? (Array.isArray(requiredPermission) ? requiredPermission[0] : requiredPermission)
    : '';

  // Always call the hook (React rules)
  const accessResult = useAccessControl(permission);

  // If no permission requirement, allow access
  if (!requiredPermission) {
    return { allowed: true, reason: 'allowed' };
  }

  return accessResult;
};

export default useAccessControl;
